import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2, Save, Car, Bike, Plus, X, Image } from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { vehiclesApi } from '../../lib/api'

export default function VehicleEditor() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { language } = useLanguageStore()

    const isEdit = !!id

    const [formData, setFormData] = useState({
        type: 'motorcycle',
        brand: '',
        model: '',
        year: new Date().getFullYear(),
        license_plate: '',
        color: '',
        seats: 2,
        transmission: 'automatic',
        fuel_type: 'gasoline',
        daily_rate: 0,
        hourly_rate: 0,
        location_address: '',
        status: 'active',
        title_en: '',
        title_vi: '',
        description_en: '',
        description_vi: '',
    })

    // Features as arrays for easier editing
    const [featuresEn, setFeaturesEn] = useState<string[]>([''])
    const [featuresVi, setFeaturesVi] = useState<string[]>([''])

    // Images as array of URLs
    const [images, setImages] = useState<string[]>([''])

    const [activeTab, setActiveTab] = useState<'en' | 'vi'>('vi')
    const [saving, setSaving] = useState(false)
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

    useEffect(() => {
        if (isEdit && id) {
            setLoading(true)
            vehiclesApi.getById(parseInt(id))
                .then((response) => {
                    const data = response.data?.data
                    if (data) {
                        setFormData({
                            type: data.type || 'motorcycle',
                            brand: data.brand || '',
                            model: data.model || '',
                            year: data.year || new Date().getFullYear(),
                            license_plate: data.license_plate || '',
                            color: data.color || '',
                            seats: data.seats || 2,
                            transmission: data.transmission || 'automatic',
                            fuel_type: data.fuel_type || 'gasoline',
                            daily_rate: data.daily_rate || 0,
                            hourly_rate: data.hourly_rate || 0,
                            location_address: data.location_address || '',
                            status: data.status || 'active',
                            title_en: data.title_en || '',
                            title_vi: data.title_vi || '',
                            description_en: data.description_en || '',
                            description_vi: data.description_vi || '',
                        })

                        // Parse features from JSON or string
                        try {
                            const featEn = data.features_en ? (typeof data.features_en === 'string' ? JSON.parse(data.features_en) : data.features_en) : []
                            setFeaturesEn(featEn.length > 0 ? featEn : [''])
                        } catch { setFeaturesEn(['']) }

                        try {
                            const featVi = data.features_vi ? (typeof data.features_vi === 'string' ? JSON.parse(data.features_vi) : data.features_vi) : []
                            setFeaturesVi(featVi.length > 0 ? featVi : [''])
                        } catch { setFeaturesVi(['']) }

                        // Parse images
                        if (data.media && Array.isArray(data.media)) {
                            const urls = data.media.map((m: any) => m.url).filter(Boolean)
                            setImages(urls.length > 0 ? urls : [''])
                        } else if (data.images && Array.isArray(data.images)) {
                            setImages(data.images.length > 0 ? data.images : [''])
                        }
                    }
                })
                .catch((err) => console.error('Failed to fetch vehicle:', err))
                .finally(() => setLoading(false))
        }
    }, [id, isEdit])

    const handleSave = async () => {
        setSaving(true)
        setMessage(null)

        // Filter out empty values and convert to JSON
        const features_en = JSON.stringify(featuresEn.filter(f => f.trim()))
        const features_vi = JSON.stringify(featuresVi.filter(f => f.trim()))
        const imageUrls = images.filter(i => i.trim())

        const dataToSend = {
            ...formData,
            features_en,
            features_vi,
            images: imageUrls,
        }

        try {
            if (isEdit && id) {
                const response = await vehiclesApi.adminUpdate(parseInt(id), dataToSend)
                if (response.error) throw new Error(response.error)
                setMessage({ type: 'success', text: language === 'en' ? 'Vehicle saved!' : 'Xe đã được lưu!' })
            } else {
                const response = await vehiclesApi.adminCreate(dataToSend)
                if (response.error) throw new Error(response.error)
                setMessage({ type: 'success', text: language === 'en' ? 'Vehicle created!' : 'Xe đã được tạo!' })
                if (response.data?.id) {
                    navigate(`/admin/vehicles/${response.data.id}/edit`)
                }
            }
        } catch (error) {
            console.error('Failed to save:', error)
            setMessage({ type: 'error', text: language === 'en' ? 'Failed to save' : 'Lưu thất bại' })
        } finally {
            setSaving(false)
        }
    }

    // Feature list helpers
    const addFeature = (lang: 'en' | 'vi') => {
        if (lang === 'en') {
            setFeaturesEn([...featuresEn, ''])
        } else {
            setFeaturesVi([...featuresVi, ''])
        }
    }

    const removeFeature = (lang: 'en' | 'vi', index: number) => {
        if (lang === 'en') {
            setFeaturesEn(featuresEn.filter((_, i) => i !== index))
        } else {
            setFeaturesVi(featuresVi.filter((_, i) => i !== index))
        }
    }

    const updateFeature = (lang: 'en' | 'vi', index: number, value: string) => {
        if (lang === 'en') {
            const updated = [...featuresEn]
            updated[index] = value
            setFeaturesEn(updated)
        } else {
            const updated = [...featuresVi]
            updated[index] = value
            setFeaturesVi(updated)
        }
    }

    // Image helpers
    const addImage = () => setImages([...images, ''])
    const removeImage = (index: number) => setImages(images.filter((_, i) => i !== index))
    const updateImage = (index: number, value: string) => {
        const updated = [...images]
        updated[index] = value
        setImages(updated)
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        )
    }

    const currentFeatures = activeTab === 'en' ? featuresEn : featuresVi

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/vehicles')} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            {isEdit
                                ? (language === 'en' ? 'Edit Vehicle' : 'Sửa Xe')
                                : (language === 'en' ? 'New Vehicle' : 'Thêm Xe Mới')
                            }
                        </h1>
                    </div>
                </div>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {language === 'en' ? 'Save' : 'Lưu'}
                </button>
            </div>

            {message && (
                <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {message.text}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Basic Info */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            {formData.type === 'car' ? <Car className="w-5 h-5" /> : <Bike className="w-5 h-5" />}
                            {language === 'en' ? 'Basic Information' : 'Thông tin cơ bản'}
                        </h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Type' : 'Loại xe'}</label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="motorcycle">{language === 'en' ? 'Motorcycle' : 'Xe máy'}</option>
                                    <option value="car">{language === 'en' ? 'Car' : 'Ô tô'}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Brand' : 'Hãng'}</label>
                                <input
                                    type="text"
                                    value={formData.brand}
                                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                                    placeholder="Honda, Toyota..."
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Model' : 'Model'}</label>
                                <input
                                    type="text"
                                    value={formData.model}
                                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                                    placeholder="Wave, Vios..."
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Year' : 'Năm SX'}</label>
                                <input
                                    type="number"
                                    value={formData.year}
                                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Color' : 'Màu sắc'}</label>
                                <input
                                    type="text"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    placeholder="Đen, Trắng, Đỏ..."
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Seats' : 'Số chỗ'}</label>
                                <input
                                    type="number"
                                    value={formData.seats}
                                    onChange={(e) => setFormData({ ...formData, seats: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Transmission' : 'Hộp số'}</label>
                                <select
                                    value={formData.transmission}
                                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="automatic">{language === 'en' ? 'Automatic' : 'Tự động'}</option>
                                    <option value="manual">{language === 'en' ? 'Manual' : 'Số sàn'}</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Fuel Type' : 'Nhiên liệu'}</label>
                                <select
                                    value={formData.fuel_type}
                                    onChange={(e) => setFormData({ ...formData, fuel_type: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                >
                                    <option value="gasoline">{language === 'en' ? 'Gasoline' : 'Xăng'}</option>
                                    <option value="diesel">{language === 'en' ? 'Diesel' : 'Dầu'}</option>
                                    <option value="electric">{language === 'en' ? 'Electric' : 'Điện'}</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Translations */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setActiveTab('vi')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'vi' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                🇻🇳 Tiếng Việt
                            </button>
                            <button
                                onClick={() => setActiveTab('en')}
                                className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === 'en' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                🇬🇧 English
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {activeTab === 'en' ? 'Title (English)' : 'Tiêu đề (Tiếng Việt)'}
                                </label>
                                <input
                                    type="text"
                                    value={activeTab === 'en' ? formData.title_en : formData.title_vi}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'title_en' : 'title_vi']: e.target.value
                                    })}
                                    placeholder={activeTab === 'en' ? 'Vehicle title...' : 'Tiêu đề xe...'}
                                    className="w-full px-4 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">
                                    {activeTab === 'en' ? 'Description (English)' : 'Mô tả (Tiếng Việt)'}
                                </label>
                                <textarea
                                    value={activeTab === 'en' ? formData.description_en : formData.description_vi}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        [activeTab === 'en' ? 'description_en' : 'description_vi']: e.target.value
                                    })}
                                    placeholder={activeTab === 'en' ? 'Vehicle description...' : 'Mô tả xe...'}
                                    rows={4}
                                    className="w-full px-4 py-2 border rounded-lg"
                                />
                            </div>

                            {/* Features List with +/- buttons */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {activeTab === 'en' ? 'Features (English)' : 'Tính năng (Tiếng Việt)'}
                                </label>
                                <div className="space-y-2">
                                    {currentFeatures.map((feature, index) => (
                                        <div key={index} className="flex gap-2">
                                            <input
                                                type="text"
                                                value={feature}
                                                onChange={(e) => updateFeature(activeTab, index, e.target.value)}
                                                placeholder={activeTab === 'en' ? `Feature ${index + 1}...` : `Tính năng ${index + 1}...`}
                                                className="flex-1 px-3 py-2 border rounded-lg"
                                            />
                                            <button
                                                onClick={() => removeFeature(activeTab, index)}
                                                disabled={currentFeatures.length <= 1}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                                                title="Remove"
                                            >
                                                <X className="w-5 h-5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        onClick={() => addFeature(activeTab)}
                                        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                    >
                                        <Plus className="w-4 h-4" />
                                        {activeTab === 'en' ? 'Add Feature' : 'Thêm tính năng'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Images */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4 flex items-center gap-2">
                            <Image className="w-5 h-5" />
                            {language === 'en' ? 'Vehicle Images' : 'Ảnh xe'}
                        </h3>
                        <div className="space-y-3">
                            {images.map((url, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                    <div className="flex-1">
                                        <input
                                            type="text"
                                            value={url}
                                            onChange={(e) => updateImage(index, e.target.value)}
                                            placeholder="https://example.com/image.jpg"
                                            className="w-full px-3 py-2 border rounded-lg"
                                        />
                                        {url && (
                                            <img
                                                src={url}
                                                alt={`Preview ${index + 1}`}
                                                className="mt-2 h-20 w-32 object-cover rounded-lg border"
                                                onError={(e) => (e.currentTarget.style.display = 'none')}
                                            />
                                        )}
                                    </div>
                                    <button
                                        onClick={() => removeImage(index)}
                                        disabled={images.length <= 1}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Remove"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={addImage}
                                className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                            >
                                <Plus className="w-4 h-4" />
                                {language === 'en' ? 'Add Image' : 'Thêm ảnh'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Pricing */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Pricing' : 'Giá thuê'}</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Daily Rate (VND)' : 'Giá ngày (VND)'}</label>
                                <input
                                    type="number"
                                    value={formData.daily_rate}
                                    onChange={(e) => setFormData({ ...formData, daily_rate: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{language === 'en' ? 'Hourly Rate (VND)' : 'Giá giờ (VND)'}</label>
                                <input
                                    type="number"
                                    value={formData.hourly_rate}
                                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseInt(e.target.value) || 0 })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Status' : 'Trạng thái'}</h3>
                        <select
                            value={formData.status}
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                            className="w-full px-3 py-2 border rounded-lg"
                        >
                            <option value="active">{language === 'en' ? 'Active' : 'Hoạt động'}</option>
                            <option value="pending">{language === 'en' ? 'Pending' : 'Chờ duyệt'}</option>
                            <option value="inactive">{language === 'en' ? 'Inactive' : 'Tạm dừng'}</option>
                        </select>
                    </div>

                    {/* Location */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'Location' : 'Vị trí'}</h3>
                        <input
                            type="text"
                            value={formData.location_address}
                            onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                            placeholder={language === 'en' ? 'Address...' : 'Địa chỉ...'}
                            className="w-full px-3 py-2 border rounded-lg"
                        />
                    </div>

                    {/* License Plate */}
                    <div className="bg-white rounded-xl shadow-sm border p-6">
                        <h3 className="font-bold mb-4">{language === 'en' ? 'License Plate' : 'Biển số'}</h3>
                        <input
                            type="text"
                            value={formData.license_plate}
                            onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                            placeholder="79C1-12345"
                            className="w-full px-3 py-2 border rounded-lg font-mono"
                        />
                    </div>
                </div>
            </div>
        </div>
    )
}
